import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import * as api from '../api/endpoints';

// Keep in sync with MAX_BROADCAST_ATTACHMENTS_BYTES in customers/views.py.
const MAX_ATTACHMENTS_BYTES = 20 * 1024 * 1024;

const formatSize = (bytes) => (bytes < 1024 * 1024 ? `${Math.ceil(bytes / 1024)} KB` : `${(bytes / (1024 * 1024)).toFixed(1)} MB`);

/**
 * Admin-only "compose and send" tool: write one email and send it to a
 * chosen subset of customers, each as their own individual message (never
 * one email with everyone in Bcc, so nobody sees anyone else's address).
 * Supports a {customer_name} placeholder for a per-recipient personal
 * touch, same simple substitution style as the ticket email templates.
 * Any attached files go out identically on every recipient's copy.
 */
const ComposeBroadcastEmailModal = ({ onClose }) => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('all'); // 'all' | 'active' | 'inactive'
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const totalAttachmentBytes = files.reduce((sum, f) => sum + f.size, 0);
  const attachmentsTooLarge = totalAttachmentBytes > MAX_ATTACHMENTS_BYTES;

  const handleFilesChosen = (e) => {
    const chosen = Array.from(e.target.files || []);
    setFiles((prev) => [...prev, ...chosen]);
    e.target.value = ''; // lets picking the exact same file again register as a change
  };

  const removeFile = (index) => setFiles((prev) => prev.filter((_, i) => i !== index));

  useEffect(() => {
    api.getAllCustomers()
      .then(setCustomers)
      .catch(() => toast.error('Could not load customers.'))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return customers.filter((c) => {
      if (activeFilter === 'active' && !c.is_active) return false;
      if (activeFilter === 'inactive' && c.is_active) return false;
      if (!q) return true;
      return `${c.display_name} ${c.email}`.toLowerCase().includes(q);
    });
  }, [customers, query, activeFilter]);

  const toggle = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAllShown = () => setSelectedIds((prev) => new Set([...prev, ...filtered.map((c) => c.id)]));
  const clearSelection = () => setSelectedIds(new Set());

  const handleSend = async () => {
    if (selectedIds.size === 0) { toast.error('Select at least one customer.'); return; }
    if (!subject.trim() || !body.trim()) { toast.error('Subject and body are both required.'); return; }
    if (attachmentsTooLarge) { toast.error(`Attachments are too large (${formatSize(totalAttachmentBytes)}) -- keep them under ${formatSize(MAX_ATTACHMENTS_BYTES)} combined.`); return; }
    if (!confirming) { setConfirming(true); return; }
    try {
      setSending(true);
      const result = await api.sendBroadcastEmail({ customerIds: Array.from(selectedIds), subject, body, files });
      const skippedNote = result.skipped?.length ? ` Skipped (no email on file): ${result.skipped.join(', ')}` : '';
      toast.success(`Sent to ${result.sent_count} customer${result.sent_count === 1 ? '' : 's'}.${skippedNote}`);
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Could not send the email.');
      setConfirming(false);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 780 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 style={{ margin: 0 }}>Compose email</h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">&times;</button>
        </div>

        <p style={{ fontSize: '0.8rem', color: '#aab7c4' }}>
          Write one email and send it to whichever customers you pick below -- each gets their own individual copy.
          Use <code>{'{customer_name}'}</code> in the subject or body to personalise it per recipient.
        </p>

        <div className="modal-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr', gap: '1rem' }}>
          <div>
            <div className="section-title">Recipients ({selectedIds.size} selected)</div>
            <input
              className="form-input" style={{ marginBottom: '0.4rem' }}
              placeholder="Search customers by name or email..." value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <div style={{ display: 'flex', gap: '0.3rem', marginBottom: '0.4rem' }} role="group" aria-label="Filter by verification status">
              {[
                { value: 'all', label: 'All' },
                { value: 'active', label: 'Active' },
                { value: 'inactive', label: 'Not active' },
              ].map((opt) => (
                <button
                  key={opt.value} type="button"
                  className={activeFilter === opt.value ? 'btn-primary' : 'btn-secondary'}
                  style={{ padding: '0.25rem 0.6rem', fontSize: '0.78rem' }}
                  onClick={() => setActiveFilter(opt.value)}
                  aria-pressed={activeFilter === opt.value}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <p style={{ fontSize: '0.72rem', color: '#aab7c4', margin: '0 0 0.4rem' }}>
              "Active" means their Mavis account was email-verified within the last 30 days.
            </p>
            <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.4rem' }}>
              <button type="button" className="btn-secondary" onClick={selectAllShown}>Select all shown</button>
              <button type="button" className="btn-secondary" onClick={clearSelection}>Clear</button>
            </div>
            <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', padding: '0.4rem' }}>
              {loading ? <p>Loading...</p> : filtered.length === 0 ? (
                <p style={{ color: '#aab7c4', fontSize: '0.85rem' }}>No customers found.</p>
              ) : filtered.map((c) => (
                <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.25rem 0', fontSize: '0.85rem', cursor: 'pointer' }}>
                  <input type="checkbox" checked={selectedIds.has(c.id)} onChange={() => toggle(c.id)} />
                  <span>{c.display_name} <span style={{ color: '#aab7c4' }}>({c.email})</span></span>
                  {c.is_active ? (
                    <span className="label-chip" style={{ background: 'var(--success-soft-bg)', color: '#7ee6ab' }}>Active</span>
                  ) : (
                    <span className="label-chip" style={{ background: 'rgba(255,255,255,0.06)', color: '#aab7c4' }}>Not active</span>
                  )}
                </label>
              ))}
            </div>
          </div>
          <div>
            <div className="section-title">Message</div>
            <input
              className="form-input" style={{ marginBottom: '0.4rem' }}
              placeholder="Subject, e.g. Hi {customer_name}, a quick update" value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
            <textarea
              className="form-textarea" style={{ minHeight: 220, marginBottom: '0.5rem' }}
              placeholder={'Body, e.g.\nDear {customer_name},\n\nWe wanted to let you know...'}
              value={body} onChange={(e) => setBody(e.target.value)}
            />

            <div className="section-title" style={{ marginTop: 0 }}>Attachments (optional)</div>
            <input type="file" multiple onChange={handleFilesChosen} style={{ fontSize: '0.85rem', marginBottom: '0.4rem' }} />
            {files.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', marginBottom: '0.3rem' }}>
                {files.map((file, i) => (
                  <div key={`${file.name}-${i}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.82rem' }}>
                    <span>{file.name} <span style={{ color: '#aab7c4' }}>({formatSize(file.size)})</span></span>
                    <button type="button" className="btn-secondary" style={{ padding: '0.1rem 0.5rem' }} onClick={() => removeFile(i)}>Remove</button>
                  </div>
                ))}
              </div>
            )}
            {files.length > 0 && (
              <p style={{ fontSize: '0.78rem', color: attachmentsTooLarge ? '#e05555' : '#aab7c4', margin: 0 }}>
                Total: {formatSize(totalAttachmentBytes)}{attachmentsTooLarge ? ` -- over the ${formatSize(MAX_ATTACHMENTS_BYTES)} limit, remove something` : ''}
              </p>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '0.5rem', marginTop: '1rem' }}>
          {confirming ? (
            <>
              <span style={{ fontSize: '0.85rem', color: '#f0a500' }}>
                Send to {selectedIds.size} customer{selectedIds.size === 1 ? '' : 's'} now?
              </span>
              <button type="button" className="btn-secondary" onClick={() => setConfirming(false)} disabled={sending}>Cancel</button>
              <button type="button" className="btn-primary" onClick={handleSend} disabled={sending}>{sending ? 'Sending...' : 'Yes, send'}</button>
            </>
          ) : (
            <button type="button" className="btn-primary" onClick={handleSend}>Review &amp; send</button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ComposeBroadcastEmailModal;
